import styled from '@emotion/styled'
import { Props as ImageProps } from 'app/components/Common/Image'
import { Separator } from 'app/components/Common/Separator'
import { FlexBox } from 'app/components/Layout/FlexBox'
import React, { memo } from 'react'
import LazyLoad from 'react-lazyload'
import Fade from 'react-reveal/Fade'

import { Props as ServiceProps, Service } from './Service'

export interface Props {
  image?: ImageProps[]
  services?: ServiceProps[]
  title?: string
}

export const ServicesList = memo(function ServicesList({
  image,
  services,
  title,
}: Props) {
  return (
    <Container>
      <Separator />
      <Fade bottom distance="3.75rem">
        <Wrapper dial={5} row stretch wrap>
          <LeftSide>
            {image ? (
              <LazyLoad>
                <Background style={{ backgroundImage: `url(${image})` }} />
              </LazyLoad>
            ) : null}
            {title ? <Title>{title}</Title> : null}
          </LeftSide>
          <RightSide dial={4}>
            {services && services.length > 0 ? (
              <Checklist row wrap>
                {services?.map((item, index) => (
                  <Service key={index} {...item} />
                ))}
              </Checklist>
            ) : null}
          </RightSide>
        </Wrapper>
      </Fade>
    </Container>
  )
})

const Container = styled.section`
  margin-bottom: 9.75rem;

  @media (max-width: 1023px) {
    margin-bottom: 7.5rem;
  }
`

const Wrapper = styled(FlexBox)``

const LeftSide = styled.div`
  width: 40%;
  min-height: 44.4vh;
  padding-left: 1.5rem;
  position: relative;

  @media (max-width: 1023px) {
    min-height: auto;
  }

  @media (max-width: 767px) {
    width: 100%;
    padding-left: 0;
  }
`

const Background = styled.div`
  width: 100%;
  height: 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  position: absolute;
  top: 0;
  left: 0;

  @media (max-width: 767px) {
    display: none;
  }
`

const Title = styled.h3`
  max-width: 18.8125rem;
  background: ${({ theme }) => theme.colors.variants.primaryDark1};
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.375rem;
  font-weight: 500;
  line-height: 2rem;
  padding: 3.75rem;
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);

  @media (max-width: 1023px) {
    padding: 2.25rem;
  }

  @media (max-width: 767px) {
    max-width: none;
    padding: 1.5rem;
    position: relative;
    text-align: center;
    top: auto;
    transform: none;
  }
`

const RightSide = styled(FlexBox)`
  width: 60%;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  padding: 3.25rem 9.306vw 5.75rem;

  @media (max-width: 1023px) {
    padding: 2.25rem 3rem 3.75rem;
  }

  @media (max-width: 767px) {
    width: 100%;
    padding: 1.5rem 1.5rem 3rem;
  }
`

const Checklist = styled(FlexBox)`
  max-width: 32vw;

  @media (max-width: 1023px) {
    max-width: none;
  }
`
